import { DailyReport } from "@/components/DailyReport";
import { WeeklyReport } from "@/components/WeeklyReport";
import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, num, rupee, pnlClass, istDateTime, spreadText, dirClass } from "@/lib/api";
import { Stat } from "@/components/Stat";

const pf = (v) => (v === null || v === undefined ? "—" : v >= 999 ? "∞" : num(v));
const dur = (a, b) => {
  const m = Math.round((new Date(b) - new Date(a)) / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

export default function LedgerTab() {
  const [trades, setTrades] = useState([]);
  const [sum, setSum] = useState(null);

  const load = useCallback(() => {
    api.get("/paper/trades").then((r) => setTrades(r.data));
    api.get("/paper/summary").then((r) => setSum(r.data));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

  const refresh = async () => {
    try { await api.post("/paper/refresh"); load(); toast.success("Marked to market"); } catch (e) { toast.error(errMsg(e)); }
  };
  const close = async (id) => {
    try { const r = await api.post(`/paper/trades/${id}/close`); toast.success(`Closed: ${rupee(r.data.pnl)}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const open = trades.filter((t) => t.status === "open");
  const closed = trades.filter((t) => t.status === "closed");
  return (
    <div className="flex flex-col gap-3 sm:gap-4 fade-in">
      <WeeklyReport />
      <DailyReport />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Paper equity" value={rupee(sum?.equity)} sub={`capital ${rupee(sum?.capital)}`} id="stat-paper-equity" />
        <Stat label="Realized P&L" value={rupee(sum?.net_pnl)} cls={pnlClass(sum?.net_pnl)} id="stat-realized-pnl" />
        <Stat label="Live MTM" value={rupee(sum?.unrealized)} cls={pnlClass(sum?.unrealized)} id="stat-live-mtm-pnl" />
        <Stat label="Today" value={rupee(sum?.today_total)} cls={pnlClass(sum?.today_total)} sub={sum?.halted ? "HALTED — daily limit" : `limit ${rupee(-(sum?.daily_loss_limit || 0))}`} id="stat-today-pnl" />
        <Stat label="Win rate" value={`${num(sum?.win_rate)}%`} sub={`${sum?.trades ?? 0} closed trades`} id="stat-paper-win-rate" />
        <Stat label="Profit factor" value={pf(sum?.profit_factor)} cls={sum?.profit_factor >= 2 ? "up" : "text-slate-50"} sub="target ≥ 2.00" id="stat-paper-pf" />
      </div>

      <div className="panel">
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5">
          <span className="eyebrow">Open positions</span>
          <Button data-testid="button-refresh-mtm" size="sm" variant="ghost" onClick={refresh} className="h-7 gap-1 text-xs text-slate-400"><RefreshCw size={12} /> Mark to market</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="dense num w-full" data-testid="table-paper-open-positions">
            <thead><tr><th>Opened</th><th>Spread</th><th>Lots</th><th>Entry</th><th>Current</th><th>Stop</th><th>Target</th><th>Risk</th><th>MTM</th><th></th></tr></thead>
            <tbody>
              {open.length === 0 && <tr><td colSpan={10} className="!py-6 !text-center text-slate-500">No open paper positions.</td></tr>}
              {open.map((t) => (
                <tr key={t.id} data-testid={`open-row-${t.id}`}>
                  <td>{istDateTime(t.opened_at)}</td>
                  <td className={dirClass(t.direction)} data-testid={`open-spread-${t.id}`}>{t.legs && <span className="mr-1 text-slate-400">{t.strategy}</span>}{spreadText(t)} <span className="text-slate-500">{t.auto ? "auto" : ""}</span></td>
                  <td>{t.lots}×{t.lot_size}</td><td>{num(t.debit)}</td><td>{num(t.current_value)}</td>
                  <td className="down">{num(t.stop_value)}</td><td className="up">{num(t.target_value)}</td><td>{t.hold_to_expiry ? `to ${t.expiry}` : rupee(t.planned_risk)}</td>
                  <td className={pnlClass(t.unrealized)}>{rupee(t.unrealized)}</td>
                  <td><Button data-testid={`button-close-position-${t.id}`} size="sm" variant="outline" onClick={() => close(t.id)} className="h-6 border-slate-700 px-2 text-[11px]">Close</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="border-b border-slate-800 px-4 py-2.5"><span className="eyebrow">Trade history</span></div>
        <div className="max-h-[460px] overflow-auto">
          <table className="dense num w-full" data-testid="table-paper-trade-history">
            <thead className="sticky top-0 bg-[#0D121F]"><tr><th>Closed</th><th>Spread</th><th>Entry</th><th>Exit</th><th>Held</th><th>Exit reason</th><th>P&amp;L</th><th>Result</th></tr></thead>
            <tbody>
              {closed.length === 0 && <tr><td colSpan={8} className="!py-6 !text-center text-slate-500">No closed paper trades yet.</td></tr>}
              {closed.map((t) => (
                <tr key={t.id}>
                  <td>{istDateTime(t.closed_at)}</td>
                  <td>{t.strategy} {t.legs ? spreadText(t) : `${t.buy_strike}/${t.sell_strike}`}</td>
                  <td>{num(t.debit)}</td><td>{num(t.exit_value)}</td><td>{dur(t.opened_at, t.closed_at)}</td>
                  <td className="uppercase text-slate-400">{t.exit_reason?.replace("_", " ")}</td>
                  <td className={pnlClass(t.pnl)}>{rupee(t.pnl)}</td>
                  <td className={t.pnl > 0 ? "up" : "down"}>{t.pnl > 0 ? "WIN" : "LOSS"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
